import videojs from "video.js";
import "video.js/dist/video-js.css";
import plugSrc from "../assets/plug.mp4";

import {
    apiUrl,
    formatTime,
    getParameters,
    InitiateResponse,
    parseFormattedTime,
    spawnErrorOverlay,
} from "../common";
import { TimeDisplay } from "./timeDisplay";
import { fm } from "./fmWrapper";
import { registerWindowApi } from "./fmWindowApi";

const playerSeek = new BroadcastChannel("web-player-seek");
const playerTime = new BroadcastChannel("web-player-time");
export class VideoPlayer {
    private data: InitiateResponse;
    private playerElement: HTMLVideoElement;
    private videojsPlayer;

    public timeIn = 0;
    public timeOut = 0;

    private timeDisplay = new TimeDisplay(this);

    private get playUrl() {
        return `${apiUrl}/protected/play/${this.data.encryptedPlayPayload}`;
    }
    private get videoUrl() {
        return `${this.playUrl}/default`;
    }
    private get previewUrl() {
        if (this.data.hasPreview) {
            return `${this.playUrl}/preview`;
        }
    }
    private get subtitlesOriginalUrl() {
        return `${this.playUrl}/subtitleOriginal`;
    }
    private get subtitlesEnglishUrl() {
        return `${this.playUrl}/subtitleTranslated`;
    }

    private get sources() {
        return [
            {
                src: this.videoUrl,
                type: this.data.mimeType,
            },
            {
                src: this.videoUrl,
                type: "video/mp4",
            },
        ];
    }
    private get tracks() {
        const result = [];
        if (this.data.hasOriginalSubtitles) {
            result.push({
                src: this.subtitlesOriginalUrl,
                kind: "subtitles",
                label: "Original",
            } as const);
        }
        if (this.data.hasEnglishSubtitles) {
            result.push({
                src: this.subtitlesEnglishUrl,
                kind: "subtitles",
                label: "English",
            } as const);
        }
        return result;
    }

    public get currentTime() {
        return this.videojsPlayer?.currentTime() || 0;
    }
    public set currentTime(value: number) {
        this.videojsPlayer.currentTime(value);
    }
    private get duration() {
        return this.videojsPlayer.duration() || 0;
    }

    constructor(data: InitiateResponse, socket: HTMLDivElement) {
        this.data = data;
        this.playerElement = this.createPlayer(socket);
        this.videojsPlayer = this.loadPlayer();
        this.initPlayer();

        if (data.mimeType.startsWith("audio")) {
            this.initBackgroundVideo(socket);
        }

        const { hideControls } = getParameters();
        this.timeDisplay.hidden = hideControls;
        registerWindowApi(this);
        playerSeek.onmessage = (event) => {
            const time = event.data;
            if (time !== undefined) {
                if (typeof time !== "number") {
                    console.warn(
                        "Received non-numeric time from playerSeek:",
                        time
                    );
                    return;
                }
                this.currentTime = time / 1000; // Convert milliseconds to seconds
            }
        };
        this.videojsPlayer.on("timeupdate", () => {
            playerTime.postMessage(this.currentTime * 1000);
        });
    }

    private createPlayer(socket: HTMLDivElement) {
        const videoElement = document.createElement("video");
        videoElement.id = "video-player";
        videoElement.className =
            "video-js vjs_video_3-dimensions vjs-controls-enabled vjs-workinghover vjs-layout-large vjs-big-play-centered";
        videoElement.controls = true;
        videoElement.preload = "auto";
        const element = socket.appendChild(videoElement);
        return element;
    }

    private loadPlayer() {
        const { watermark } = getParameters();

        const player = videojs(this.playerElement, {
            poster: this.previewUrl,
            sources: this.sources,
            disablePictureInPicture: !!watermark?.length,
            tracks: this.tracks,
            preload: "metadata",
            autoplay: false,
            playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3, 10],
            controlBar: {
                fullscreenToggle: false,
                skipButtons: {
                    forward: 5,
                    backward: 5,
                },
            },
            userActions: {
                doubleClick: false,
                hotkeys: (event: KeyboardEvent) => {
                    switch (event.code) {
                        case "KeyA":
                            this.currentTime -= 5;
                            break;
                        case "KeyS":
                            this.videojsPlayer.paused()
                                ? this.videojsPlayer.play()
                                : this.videojsPlayer.pause();
                            break;
                        case "KeyD":
                            this.currentTime += 5;
                            break;
                    }
                },
            },
            inactivityTimeout: 0,
        });
        return player;
    }

    public setTimeOut(value: string | null = null) {
        this.timeOut = value ? parseFormattedTime(value) : this.currentTime;
        this.syncPayload();
    }

    public setTimeIn(value: string | null = null) {
        this.timeIn = value ? parseFormattedTime(value) : this.currentTime;
        this.syncPayload();
    }

    public clearTimes() {
        this.timeIn = 0;
        this.timeOut = this.duration;
        this.syncPayload();
    }

    private initPlayer() {
        this.videojsPlayer.on("loadedmetadata", () => {
            const { inTc, outTc } = getParameters();
            this.timeIn = inTc ? parseFormattedTime(inTc) : 0;
            this.timeOut = outTc ? parseFormattedTime(outTc) : this.duration;
            this.currentTime = this.timeIn ? this.timeIn : 0;
            fm.onPlayerReady();
        });
        this.playerElement.addEventListener("error", () => {
            spawnErrorOverlay(
                JSON.stringify({
                    error: "Internal video player error",
                    details: this.videojsPlayer.error(),
                    contentType: this.data.mimeType,
                })
            );
        });

        this.videojsPlayer.on("timeupdate", () => this.timeDisplay.update());
    }

    private initBackgroundVideo(socket: HTMLDivElement) {
        const background = document.createElement("video");
        background.id = "background-video";
        background.muted = true;
        background.loop = true;
        background.playsInline = true;
        const source = document.createElement("source");
        source.src = plugSrc;
        source.type = "video/mp4";
        background.appendChild(source);
        socket.appendChild(background);
        this.videojsPlayer.on("play", () => {
            background.play();
        });
        this.videojsPlayer.on("pause", () => {
            background.pause();
        });
    }

    public syncPayload(functionName: string | null = null) {
        const payload = {
            timeIn: formatTime(this.timeIn),
            timeOut: formatTime(this.timeOut),
            currentTime: formatTime(this.currentTime),
        };
        fm.executeSync(payload, functionName);
        this.timeDisplay.update();
    }

    public pause() {
        this.videojsPlayer.pause();
    }
}
